import { enumMembers } from "../../apiSchema";
import { requestJson } from "../../http/httpClient";
import { readArray, readNullableString, readObject, readOneOf, readString } from "../../http/jsonDecoding";
import { ITEM_KINDS, type ItemKind } from "../reviewDecoders";
import { decodeMadeBy, type ItemMadeBy } from "./processAuthoringApi";

/**
 * An item's usage (authoring-ui.md AU-5, build plan B5), from
 * `GET /projects/{project}/content/items/by-id/{item}/usage`: every version `main` held with
 * who or what made it, and the steps, skills and Workflows that read the item now.
 */

export type ItemReadKind = "selector" | "bundled" | "skill" | "step" | "sub_workflow";

const READ_KINDS = enumMembers<ItemReadKind>({ selector: true, bundled: true, skill: true, step: true, sub_workflow: true });

export interface ItemVersionOrigin {
  readonly reference: string;
  /** The model version (commit) that brought this version. */
  readonly introducedIn: string;
  /** `null` if no merge brought it (seeded). */
  readonly madeBy: ItemMadeBy | null;
}

export interface ItemReader {
  readonly kind: ItemKind;
  readonly reference: string;
  readonly name: string;
  readonly readKind: ItemReadKind;
  /** For selectors and bundled files: the pinned version; `null` if it floats. */
  readonly selectedVersion: string | null;
  /** For skills, steps and sub-workflows: the pinned version that reads the item. */
  readonly via: string | null;
}

export interface ItemUsage {
  readonly modelVersion: string;
  readonly reference: string;
  /** Newest first. */
  readonly versions: readonly ItemVersionOrigin[];
  readonly readBy: readonly ItemReader[];
}

export function readItemUsage(projectId: string, itemId: string, signal?: AbortSignal): Promise<ItemUsage> {
  return requestJson({
    method: "GET",
    path: `/projects/${encodeURIComponent(projectId)}/content/items/by-id/${encodeURIComponent(itemId)}/usage`,
    signal,
    decodeResponse: (body) => {
      const usage = readObject(body, "item usage");
      return {
        modelVersion: readString(usage, "model_version"),
        reference: readString(usage, "reference"),
        versions: readArray(usage["versions"], "versions", (value) => {
          const version = readObject(value, "version origin");
          return { reference: readString(version, "reference"), introducedIn: readString(version, "introduced_in"), madeBy: decodeMadeBy(version["made_by"]) };
        }),
        readBy: readArray(usage["read_by"], "readers", (value) => {
          const reader = readObject(value, "reader");
          const read = readObject(reader["read"], "read");
          return {
            kind: readOneOf(reader, "kind", ITEM_KINDS),
            reference: readString(reader, "reference"),
            name: readString(reader, "name"),
            readKind: readOneOf(read, "kind", READ_KINDS),
            selectedVersion: readNullableString(read, "selected_version"),
            via: readNullableString(read, "via"),
          };
        }),
      };
    },
  });
}
